import { OptionTypeBase } from '@cad-arc';
import { alphanumericStringComparator } from '../../../common/sorting/comparators';
import { SelectOptions } from '../../../forms/option';

// Lower rank = better match. Only applied once the user has typed a query.
const MATCH_RANK = {
    exact: 0,
    startsWith: 1,
    contains: 2,
    noMatch: 3,
};

function getMatchRank(label: string, query: string): number {
    if (label === query) {
        return MATCH_RANK.exact;
    }
    if (label.startsWith(query)) {
        return MATCH_RANK.startsWith;
    }
    if (label.includes(query)) {
        return MATCH_RANK.contains;
    }
    return MATCH_RANK.noMatch;
}

/**
 * Arc's Select truncates the option list to `maxOptionsToShow` (300 by default)
 * BEFORE it applies `matchSort`, so options past the cap are dropped and can
 * never be surfaced — this is why single-digit call signs like "4" / "9" go
 * missing in large tenants (RND-59365 / RND-67273).
 *
 * Arc invokes `sortOptions` BEFORE filtering and truncation, so we reorder the
 * full list here to guarantee the most relevant options survive the cap. This
 * only reorders — it never adds or removes options — so Arc's own filtering and
 * `matchSort` behavior are left untouched.
 *
 *  - no query (default open): numeric-aware natural order, so single-digit call
 *    signs sit at the top instead of being buried past the cap
 *  - with a query: exact -> starts-with -> contains, then natural order within
 *    each bucket
 *
 * Grouped option lists are returned unchanged (call sign options are always flat).
 */
export function sortCallSignOptions(
    options: SelectOptions | undefined,
    inputValue: string
): SelectOptions | undefined {
    if (!options) {
        return options;
    }

    // Call sign options are always a flat list. If we ever receive grouped
    // options (some were filtered out here), leave the list untouched.
    const flatOptions = options.filter(
        (option): option is OptionTypeBase => !('options' in option)
    );
    if (flatOptions.length !== options.length) {
        return options;
    }

    const query = inputValue.trim().toLowerCase();

    return flatOptions.sort((left, right) => {
        if (query) {
            const rankDelta =
                getMatchRank(left.label.toLowerCase(), query) -
                getMatchRank(right.label.toLowerCase(), query);
            if (rankDelta !== 0) {
                return rankDelta;
            }
        }
        return alphanumericStringComparator(left.label, right.label);
    });
}
